/**
 * Work Queue Page
 * Main page with 3-column layout for work queue, focus preview, and action rail
 */

import { useEffect, useState, useMemo } from 'react'
import { makeStyles, tokens, Caption1 } from '@fluentui/react-components'
import { useSetHeaderLeft } from '../../components'
import { useIssues } from '../../data'
import { useExperienceMode } from '../../context/ExperienceModeContext'
import { TopCommandHeader, WorkQueuePane, FocusPreviewPane, ActionRailPane } from './components'
import { WorkQueueFilterProvider, useWorkQueueFilter } from './context'
import {
  computeTagCounts,
  sortIssues,
  filterIssuesBySearch,
  filterIssuesByTag,
} from './utils/filterUtils'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    flex: 1,
    gap: tokens.spacingVerticalM,
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(280px, 360px) minmax(560px, 1fr) minmax(280px, 360px)',
    gap: tokens.spacingHorizontalM,
    flex: 1,
    overflow: 'hidden',
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXXS,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
})

function HeaderContent() {
  const styles = useStyles()
  return (
    <div className={styles.headerContent}>
      <span className={styles.title}>SFI Autopilot</span>
      <Caption1 className={styles.subtitle}>Intelligent automation for enterprise workflows</Caption1>
    </div>
  )
}

function WorkQueuePageContent() {
  const styles = useStyles()
  const setHeaderLeft = useSetHeaderLeft()
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState<string>('')
  const { experienceMode } = useExperienceMode()

  // Clear selection when experience mode changes
  useEffect(() => {
    setSelectedIssueId(null)
  }, [experienceMode])

  // Data state using React Query hook
  const {
    data: issuesResult,
    isLoading: loading,
    error,
    refetch: loadIssues,
  } = useIssues()
  const allIssues = issuesResult?.issues ?? null

  // Get filter state from context
  const {
    deferredSearchQuery,
    activeTagFilter,
    sortBy,
    sortDirection,
    setTagCounts,
  } = useWorkQueueFilter()

  useEffect(() => {
    setHeaderLeft(<HeaderContent />)
    return () => setHeaderLeft(null)
  }, [setHeaderLeft])

  // Compute tag counts from ALL issues (not filtered) and update context
  useEffect(() => {
    if (allIssues && allIssues.length > 0) {
      setTagCounts(computeTagCounts(allIssues))
    }
  }, [allIssues, setTagCounts])

  // Compute filtered and sorted issues
  const filteredIssues = useMemo(() => {
    if (!allIssues) return []
    let result = allIssues

    // Apply search filter
    result = filterIssuesBySearch(result, deferredSearchQuery)

    // Apply tag filter
    result = filterIssuesByTag(result, activeTagFilter)

    // Apply sort
    result = sortIssues(result, sortBy, sortDirection)

    return result
  }, [allIssues, deferredSearchQuery, activeTagFilter, sortBy, sortDirection])

  return (
    <div className={styles.container}>
      <TopCommandHeader />
      <div className={styles.mainGrid}>
        <WorkQueuePane
          issues={filteredIssues}
          loading={loading}
          error={error}
          selectedIssueId={selectedIssueId}
          onSelectIssue={setSelectedIssueId}
          onRetry={loadIssues}
          onSelectionAnnounce={setAnnouncement}
        />
        <FocusPreviewPane selectedIssueId={selectedIssueId} />
        <ActionRailPane selectedIssueId={selectedIssueId} />
      </div>
      {/* Live region for screen reader announcements */}
      <div role="status" aria-live="polite" className={styles.srOnly}>
        {announcement}
      </div>
    </div>
  )
}

export function WorkQueuePage() {
  return (
    <WorkQueueFilterProvider>
      <WorkQueuePageContent />
    </WorkQueueFilterProvider>
  )
}
